/**
 * 轨道节点组件
 * 用于显示前端/后端开发轨道状态
 */

import { type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import {
  TRACK_DISPLAY_NAMES,
  TRACK_STATUS_DISPLAY_NAMES,
  TRACK_STATUS_COLORS,
  TRACK_BORDER_COLORS,
} from '../workflow-constants'
import { formatStageTime } from '../workflow-utils'

import type { TrackType, TrackStatus } from '@company/shared'

// ==================== 类型定义 ====================

interface TrackNodeProps {
  track: TrackType
  status: TrackStatus
  startedAt?: string | null
  submittedAt?: string | null
  approvedAt?: string | null
  assigneeName?: string | null
  actions?: ReactNode
  className?: string
}

// ==================== 组件实现 ====================

export function TrackNode({
  track,
  status,
  startedAt,
  submittedAt,
  approvedAt,
  assigneeName,
  actions,
  className,
}: TrackNodeProps) {
  const trackName = TRACK_DISPLAY_NAMES[track]
  const statusColors = TRACK_STATUS_COLORS[status]
  const borderColor = TRACK_BORDER_COLORS[track]

  // 获取显示的状态文本
  const statusText = status === 'in_review' ? '代码审查中' : TRACK_STATUS_DISPLAY_NAMES[status]

  // 获取时间显示
  const getTimeDisplay = () => {
    if (status === 'approved' && approvedAt) {
      return `完成于 ${formatStageTime(approvedAt)}`
    }
    if (status === 'in_review' && submittedAt) {
      return `提交于 ${formatStageTime(submittedAt)}`
    }
    if (startedAt) {
      return `开始于 ${formatStageTime(startedAt)}`
    }
    return null
  }

  const timeDisplay = getTimeDisplay()

  return (
    <div className={cn('border-l-2 pl-2', borderColor, className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('text-xs', statusColors.text)}>
          {trackName}: {statusText}
        </span>
        {timeDisplay && <span className="text-muted-foreground text-[10px]">{timeDisplay}</span>}
      </div>
      {assigneeName && (
        <div className="text-muted-foreground mt-0.5 text-[10px]">负责人: {assigneeName}</div>
      )}
      {actions}
    </div>
  )
}

/**
 * 轨道卡片组件
 * 并排布局时使用的卡片样式
 */
interface TrackCardProps {
  track: TrackType
  status: TrackStatus
  startedAt?: string | null
  submittedAt?: string | null
  approvedAt?: string | null
  assigneeName?: string | null
  actions?: ReactNode
  className?: string
}

export function TrackCard({
  track,
  status,
  startedAt,
  submittedAt,
  approvedAt,
  assigneeName,
  actions,
  className,
}: TrackCardProps) {
  const trackName = TRACK_DISPLAY_NAMES[track]
  const statusColors = TRACK_STATUS_COLORS[status]
  const borderColor = TRACK_BORDER_COLORS[track]

  const statusText = status === 'in_review' ? '代码审查中' : TRACK_STATUS_DISPLAY_NAMES[status]

  const getTimeDisplay = () => {
    if (status === 'approved' && approvedAt) {
      return `完成于 ${formatStageTime(approvedAt)}`
    }
    if (status === 'in_review' && submittedAt) {
      return `提交于 ${formatStageTime(submittedAt)}`
    }
    if (startedAt) {
      return `开始于 ${formatStageTime(startedAt)}`
    }
    return null
  }

  const timeDisplay = getTimeDisplay()

  // 根据状态决定背景色
  const cardBgColor =
    status === 'approved'
      ? 'bg-green-50 dark:bg-green-950/20'
      : status === 'in_review'
        ? 'bg-amber-50 dark:bg-amber-950/20'
        : status === 'in_progress'
          ? 'bg-blue-50 dark:bg-blue-950/20'
          : 'bg-card'

  return (
    <div
      className={cn(
        'min-w-[140px] flex-1 rounded-lg border-2 p-4',
        borderColor,
        cardBgColor,
        className
      )}
    >
      {/* 轨道标题 */}
      <div className="mb-2 text-base font-medium">{trackName}轨道</div>

      {/* 状态徽章 */}
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
          statusColors.bg,
          statusColors.text
        )}
      >
        {statusText}
      </span>

      {/* 负责人 */}
      {assigneeName && (
        <div className="text-muted-foreground mt-3 text-sm">负责人: {assigneeName}</div>
      )}

      {/* 时间信息 */}
      {timeDisplay && <div className="text-muted-foreground mt-1 text-xs">{timeDisplay}</div>}

      {/* 操作按钮 */}
      {actions && <div className="mt-3">{actions}</div>}
    </div>
  )
}
